import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { fullscreenApi } from '../utils/fullscreen';

export interface Fullscreen {
  /** True whenever the element should be presenting, native or not. */
  active: boolean;
  nativeSupported: boolean;
  enter: () => void;
  exit: () => void;
  toggle: () => void;
}

/**
 * Presentation mode for one element.
 *
 * `active` is the app's own intent rather than the browser's state, because
 * native fullscreen can be refused — an iframe, iOS Safari — and the CSS cover
 * still has to work when it is. Esc is followed back into state, since it
 * leaves fullscreen without going through the button.
 */
export function useFullscreen<T extends HTMLElement>(ref: RefObject<T | null>): Fullscreen {
  const [active, setActive] = useState(false);
  const api = useMemo(() => fullscreenApi(document), []);

  useEffect(() => {
    // Only ever syncs *off*: with native unsupported no event fires at all, and
    // the cover has to survive that.
    const sync = () => {
      if (!api.element()) setActive(false);
    };

    api.events.forEach((event) => document.addEventListener(event, sync));
    return () => api.events.forEach((event) => document.removeEventListener(event, sync));
  }, [api]);

  const enter = useCallback(() => {
    setActive(true);

    const element = ref.current;
    // A rejection just means no native fullscreen; the cover carries it.
    if (element) void api.request(element).catch(() => undefined);
  }, [api, ref]);

  const exit = useCallback(() => {
    setActive(false);
    if (api.element()) void api.exit().catch(() => undefined);
  }, [api]);

  const toggle = useCallback(() => (active ? exit() : enter()), [active, enter, exit]);

  return { active, nativeSupported: api.supported, enter, exit, toggle };
}
